import React, { useState, useEffect } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    BottomNavigation,
    BottomNavigationAction,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Chip,
    Divider,
    Paper,
    Avatar,
} from "@mui/material";
import {
    Home as HomeIcon,
    Folder as ProjectsIcon,
    Assessment as ReportsIcon,
    CalendarMonth as ScheduleIcon,
    MoreHoriz as MoreIcon,
    AccessTime as TimesheetsIcon,
    Receipt as ExpensesIcon,
    Engineering as FieldIcon,
    BuildCircle as ShopIcon,
    Architecture as EngineeringIcon,
    BeachAccess as HolidaysIcon,
    VideoLibrary as VideosIcon,
    Campaign as AnnouncementsIcon,
    MenuBook as HandbookIcon,
    Storage as CRMIcon,
    Description as MyDocumentsIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    Close as CloseIcon,
    Settings as SettingsIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircleOutline as AccountabilitiesIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useThemeMode } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import ProfileService from "../services/Profile.service";
import kodexIcon from "../assets/kodex_icon.svg";

const bottomNavItems = [
    { label: "Reports", path: "/reports", icon: <ReportsIcon /> },
    { label: "Expenses", path: "/expenses", icon: <ExpensesIcon /> },
    { label: "Timesheets", path: "/timesheets", icon: <TimesheetsIcon /> },
    { label: "Schedule", path: "/field/schedule", icon: <ScheduleIcon /> },
    { label: "More", path: null, icon: <MoreIcon /> },
];

const drawerItems = [
    { label: "Projects", path: "/projects", icon: <ProjectsIcon /> },
    { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
    { label: "My Documents", path: "/my-documents", icon: <MyDocumentsIcon /> },
    {
        label: "Announcements",
        path: "/announcements",
        icon: <AnnouncementsIcon />,
    },
    { label: "Field", path: "/field", icon: <FieldIcon /> },
    { label: "Shop", path: "/shop", icon: <ShopIcon /> },
    { label: "Engineering", path: "/engineering", icon: <EngineeringIcon /> },
    { label: "Holidays", path: "/holidays", icon: <HolidaysIcon /> },
    { label: "My Accountabilities", path: "/accountabilities", icon: <AccountabilitiesIcon /> },
    { label: "Videos", path: "/videos", icon: <VideosIcon /> },
    { label: "Company Handbook", path: "/handbook", icon: <HandbookIcon /> },
];

const crmDrawerItem = { label: "CRM", path: "/crm", icon: <CRMIcon /> };

const pageTitles = {
    "/": "Dashboard",
    "/tasks": "Tasks",
    "/projects": "Projects",
    "/reports": "Reports",
    "/reports/incident": "Incident Report",
    "/reports/project": "Project Report",
    "/reports/door-service": "Door Service Report",
    "/reports/rf-test": "RF Test Report",
    "/timesheets": "Timesheets",
    "/expenses": "Expenses",
    "/expenses/company-card": "Company Card",
    "/expenses/reimbursable": "Reimbursable",
    "/expenses/miles": "Miles",
    "/expenses/per-diem": "Per Diem",
    "/project-tasks": "Project Tasks",
    "/announcements": "Announcements",
    "/field": "Field",
    "/field/schedule": "Schedule",
    "/field/vehicles": "Vehicles",
    "/shop": "Shop",
    "/shop/work-orders": "Work Orders",
    "/shop/equipment": "Equipment",
    "/shop/bom": "Bill of Materials",
    "/shop/material": "Material Lookup",
    "/scan": "Scan QR",
    "/engineering": "Engineering",
    "/engineering/ecns": "ECNs",
    "/engineering/schedule": "Engineering Schedule",
    "/holidays": "Holidays",
    "/accountabilities": "My Accountabilities",
    "/videos": "Videos",
    "/handbook": "Company Handbook",
    "/settings": "Settings",
    "/my-documents": "My Documents",
    "/crm": "CRM",
};

// These pages have their own internal back navigation so the top bar should
// show the logo (not the back arrow) and the specific page title.
const logoRoutes = new Set([
    "/expenses/company-card",
    "/expenses/reimbursable",
    "/expenses/miles",
    "/expenses/per-diem",
]);

const subcontractorBottomNavItems = [
    { label: "Schedule", path: "/field/schedule", icon: <ScheduleIcon /> },
    { label: "Projects", path: "/projects", icon: <ProjectsIcon /> },
    { label: "Reports", path: "/reports", icon: <ReportsIcon /> },
    { label: "Documents", path: "/my-documents", icon: <MyDocumentsIcon /> },
    { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
];

function Layout({ children }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [userPhoto, setUserPhoto] = useState(
        () => localStorage.getItem("userPhoto") || null,
    );
    const navigate = useNavigate();
    const location = useLocation();
    const { mode, toggleTheme } = useThemeMode();
    const { user } = useAuth();
    const { isSubcontractor, isAdmin, isManagement } = usePermissions();

    const activeBottomNavItems = isSubcontractor
        ? subcontractorBottomNavItems
        : bottomNavItems;

    const activeDrawerItems =
        !isSubcontractor && (isAdmin || isManagement)
            ? [...drawerItems, crmDrawerItem]
            : drawerItems;

    const userInitials = user
        ? (user.first_name?.[0] || user.username?.[0] || "?").toUpperCase()
        : "?";

    // Fetch profile photo whenever the logged-in user changes
    useEffect(() => {
        if (!user) {
            setUserPhoto(null);
            localStorage.removeItem("userPhoto");
            return;
        }
        ProfileService.getMe()
            .then((res) => {
                const imgUrl = res.data.profile?.profile_image || null;
                setUserPhoto(imgUrl);
                if (imgUrl) localStorage.setItem("userPhoto", imgUrl);
                else localStorage.removeItem("userPhoto");
            })
            .catch(() => {
                // Don't trust a cached URL after a failed refetch — it may have an
                // expired S3 signature. Fall back to initials instead.
                localStorage.removeItem("userPhoto");
                setUserPhoto(null);
            });
    }, [user?.id]);

    // Refresh photo when Profile page fires an update event
    useEffect(() => {
        const handler = () =>
            setUserPhoto(localStorage.getItem("userPhoto") || null);
        window.addEventListener("userPhotoUpdated", handler);
        return () => window.removeEventListener("userPhotoUpdated", handler);
    }, []);

    const currentTitle = pageTitles[location.pathname] || "Kodex";
    const isSubRoute =
        location.pathname.split("/").filter(Boolean).length > 1 &&
        !logoRoutes.has(location.pathname);

    const bottomNavValue = activeBottomNavItems.findIndex(
        (item) => item.path && location.pathname === item.path,
    );

    const handleBottomNav = (_, newValue) => {
        const item = activeBottomNavItems[newValue];
        if (!item) return;
        if (item.path === null) {
            setDrawerOpen(true);
        } else {
            navigate(item.path);
        }
    };

    const goTo = (path) => {
        navigate(path);
        setDrawerOpen(false);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
            }}
        >
            {/* ── Top AppBar ── */}
            <AppBar position="fixed" color="default" elevation={1}>
                <Toolbar>
                    {isSubRoute ? (
                        <IconButton
                            edge="start"
                            onClick={() => navigate(-1)}
                            aria-label="Go back"
                            sx={{ mr: 1 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    ) : (
                        <IconButton
                            edge="start"
                            onClick={() => navigate("/")}
                            aria-label="Go to dashboard"
                            sx={{ mr: 1 }}
                        >
                            <img
                                src={kodexIcon}
                                alt="Kodex"
                                style={{ height: 40, width: 40 }}
                            />
                        </IconButton>
                    )}

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 700,
                            fontSize: "1rem",
                        }}
                    >
                        {currentTitle}
                    </Typography>

                    {isSubcontractor && (
                        <Chip
                            label="Subcontractor"
                            size="small"
                            color="warning"
                            sx={{
                                ml: 1,
                                flexShrink: 0,
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 700,
                                fontSize: "0.65rem",
                            }}
                        />
                    )}
                    {!isSubcontractor && isAdmin && (
                        <Chip
                            label="Admin"
                            size="small"
                            color="secondary"
                            sx={{
                                ml: 1,
                                flexShrink: 0,
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 700,
                                fontSize: "0.65rem",
                            }}
                        />
                    )}
                    {!isSubcontractor && !isAdmin && isManagement && (
                        <Chip
                            label="Management"
                            size="small"
                            color="primary"
                            sx={{
                                ml: 1,
                                flexShrink: 0,
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 700,
                                fontSize: "0.65rem",
                            }}
                        />
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    <IconButton
                        onClick={toggleTheme}
                        aria-label={
                            mode === "dark"
                                ? "Switch to light mode"
                                : "Switch to dark mode"
                        }
                    >
                        {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
                    </IconButton>

                    <IconButton
                        edge="end"
                        aria-label="User profile"
                        sx={{ ml: 0.5 }}
                        onClick={() => navigate("/settings")}
                    >
                        <Avatar
                            src={userPhoto || undefined}
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "primary.main",
                                fontSize: "0.875rem",
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 700,
                            }}
                        >
                            {userInitials}
                        </Avatar>
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* ── Main content ── */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    mt: "64px",
                    mb: "calc(56px + env(safe-area-inset-bottom))",
                    px: 2,
                    py: 2,
                    bgcolor: "background.default",
                }}
            >
                {children}
            </Box>

            {/* ── Bottom navigation ── */}
            <Paper
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1100,
                    paddingBottom: "env(safe-area-inset-bottom)",
                    paddingLeft: "env(safe-area-inset-left)",
                    paddingRight: "env(safe-area-inset-right)",
                }}
                elevation={3}
            >
                <BottomNavigation
                    value={bottomNavValue === -1 ? false : bottomNavValue}
                    onChange={handleBottomNav}
                    showLabels
                >
                    {activeBottomNavItems.map((item) => (
                        <BottomNavigationAction
                            key={item.label}
                            label={item.label}
                            icon={item.icon}
                        />
                    ))}
                </BottomNavigation>
            </Paper>

            {/* ── More drawer (employees only) ── */}
            <Drawer
                anchor="bottom"
                open={!isSubcontractor && drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: { borderRadius: "16px 16px 0 0", maxHeight: "80vh" },
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 700,
                        }}
                    >
                        More
                    </Typography>
                    <IconButton
                        onClick={() => setDrawerOpen(false)}
                        size="small"
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <List disablePadding>
                    {activeDrawerItems.map(({ label, path, icon }) => (
                        <ListItemButton
                            key={path}
                            onClick={() => goTo(path)}
                            selected={location.pathname === path}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={label}
                                primaryTypographyProps={{
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>
        </Box>
    );
}

export default Layout;
